import {buildIconFromSVG, doubleCircleIconSVG, feederSVG, generatorSVG} from 'frontend/utils/icons';
import {MapAsset} from 'frontend/utils/map/map_asset';

/**
 * Type which represents custom marker type which can be consumed by `AssetMap` component and
 * displayed on the map.
 * Objects which implement this interface should hold logic of how they are being changed by user
 * iterations.
 */
export interface AssetMapMarker<D = AssetMapMarkerData> {
  readonly data?: D;
  setMap(map: google.maps.Map | null): void;
  highlight(enable: boolean, unselect: boolean): void;
  addListener(event: string, callback: () => void): void;
  getLocation(): google.maps.LatLng;
  setIcon(icon: google.maps.Icon): void;
  onMapZoomChange?(zoomLevel: number): void;
  getEnabledIcon(): google.maps.Icon;
  getUnselectedIcon(): google.maps.Icon;
  getHoveredIcon(): google.maps.Icon | null;
  getSelectedIcon(): google.maps.Icon;
  getHoveredUnselectedIcon(): google.maps.Icon | null;
}

/** Defines the structure for data associated with an asset map marker. */
export interface AssetMapMarkerData {
  // Optional zoom level to apply when the marker is focused.
  // If not provided, the map's current zoom level will be maintained.
  zoomLevelOnFocus?: number;
}

/** Possible icon sizes displaying on map. */
export enum IconSize {
  DEFAULT = 24,
  SELECTED = 28,
  HOVERED = 32,
}

export const UNSELECTED_ICON_OPACITY = 0.45;

const DEFAULT_ICON_FILL_COLOR = '#f1f';
const DEFAULT_ICON_STROKE_COLOR = '#333';

const HOVER_EVENT = 'mouseover';
const UNHOVER_EVENT = 'mouseout';

/** Base class for custom marker which holds common logic. */
export class DefaultMapMarker<D = AssetMapMarkerData> implements AssetMapMarker<D> {
  protected enabled: google.maps.MarkerOptions;
  protected unselected: google.maps.MarkerOptions;
  protected selected: google.maps.MarkerOptions;
  protected hovered?: google.maps.MarkerOptions;
  protected hoveredUnselected?: google.maps.MarkerOptions;

  protected enabledIcon = buildIconFromSVG('<svg></svg>');

  protected marker: google.maps.Marker;

  protected isEnable: boolean;
  protected isSelected = false;

  constructor(
    protected readonly asset: MapAsset,
    highlight = true,
    public data?: D,
  ) {
    const enabledIcon = this.getEnabledIcon();
    const selectedIcon = this.getSelectedIcon();
    const unselectedIcon = this.getUnselectedIcon();
    const hoveredIcon = this.getHoveredIcon();
    const hoveredUnselectedIcon = this.getHoveredUnselectedIcon();

    this.isEnable = highlight;

    this.enabled = {
      icon: enabledIcon,
      zIndex: 1,
    };
    this.unselected = {
      icon: unselectedIcon || enabledIcon,
      zIndex: 0,
    };
    this.selected = {
      icon: selectedIcon || enabledIcon,
      zIndex: 1,
    };
    if (hoveredIcon) {
      this.hovered = {
        icon: hoveredIcon,
        zIndex: 2,
      };
    }
    if (hoveredUnselectedIcon) {
      this.hoveredUnselected = {
        icon: hoveredUnselectedIcon,
        zIndex: 2,
      };
    }

    this.marker = new google.maps.Marker({
      position: asset.location[0],
      ...(highlight ? this.enabled : this.unselected),
    });
    if (this.hovered) {
      this.marker.addListener(HOVER_EVENT, () => {
        this.marker.setOptions(
          this.isEnable ? this.hovered! : this.hoveredUnselected || this.unselected,
        );
      });
      this.marker.addListener(UNHOVER_EVENT, () => {
        if (this.isSelected) {
          this.marker.setOptions(this.selected);
        } else {
          this.marker.setOptions(this.isEnable ? this.enabled : this.unselected);
        }
      });
    }
  }

  /**
   * Sets the custom data needed for icon manipulation.
   */
  updateData(data: D) {
    this.data = data;
    this.refreshMarker();
  }

  /**
   * Sets map to the marker.
   * To make the marker visible it should set map property.
   */
  setMap(map: google.maps.Map | null) {
    this.marker.setMap(map);
  }

  /**
   * Change icon for the marker.
   */
  setIcon(icon: google.maps.Icon) {
    this.enabledIcon = icon;
    this.marker.setIcon(icon);
  }

  /**
   * Controls the style of the marker when highlight of the marker is necessary.
   */
  highlight(enable: boolean, unselect: boolean) {
    this.isSelected = !unselect && enable;
    this.isEnable = enable;

    if (this.isSelected) {
      this.marker.setOptions(this.selected);
    } else {
      this.marker.setOptions(this.isEnable ? this.enabled : this.unselected);
    }
  }

  /**
   * Adds listener to the marker.
   */
  addListener(event: string, callback: () => void) {
    this.marker.addListener(event, callback);
  }

  /**
   * Returns location of the marker.
   */
  getLocation(): google.maps.LatLng {
    return this.asset.location[0];
  }

  refreshMarker() {
    const enabledIcon = this.getEnabledIcon();
    const selectedIcon = this.getSelectedIcon();
    const unselectedIcon = this.getUnselectedIcon();
    const hoveredIcon = this.getHoveredIcon();
    const hoveredUnselectedIcon = this.getHoveredUnselectedIcon();

    this.enabled = {
      icon: enabledIcon,
      zIndex: 1,
    };
    this.unselected = {
      icon: unselectedIcon || enabledIcon,
      zIndex: 0,
    };
    this.selected = {
      icon: selectedIcon || enabledIcon,
      zIndex: 1,
    };
    if (hoveredIcon) {
      this.hovered = {
        icon: hoveredIcon,
        zIndex: 2,
      };
    }
    if (hoveredUnselectedIcon) {
      this.hoveredUnselected = {
        icon: hoveredUnselectedIcon,
        zIndex: 2,
      };
    }

    this.marker.setOptions(this.isEnable ? this.enabled : this.unselected);
  }

  protected getDefaultIcon(size: IconSize, opacity = 1): string {
    return '<svg></svg>';
  }

  protected getRaisedIcon(size: IconSize, opacity = 1): string | null {
    return null;
  }

  getEnabledIcon(): google.maps.Icon {
    return buildIconFromSVG(this.getDefaultIcon(IconSize.DEFAULT), new google.maps.Point(12, 12));
  }

  getUnselectedIcon(): google.maps.Icon {
    const center = IconSize.DEFAULT / 2;
    return buildIconFromSVG(
      this.getDefaultIcon(IconSize.DEFAULT, UNSELECTED_ICON_OPACITY),
      new google.maps.Point(center, center),
    );
  }

  getHoveredIcon(): google.maps.Icon | null {
    const iconSvg = this.getRaisedIcon(IconSize.HOVERED);

    if (!iconSvg) {
      return null;
    }

    const center = IconSize.HOVERED / 2;
    return buildIconFromSVG(iconSvg, new google.maps.Point(center, center));
  }

  getSelectedIcon(): google.maps.Icon {
    const center = IconSize.SELECTED / 2;
    return buildIconFromSVG(
      this.getDefaultIcon(IconSize.SELECTED),
      new google.maps.Point(center, center),
    );
  }

  getHoveredUnselectedIcon(): google.maps.Icon | null {
    const iconSvg = this.getRaisedIcon(IconSize.HOVERED, UNSELECTED_ICON_OPACITY);

    if (!iconSvg) {
      return null;
    }

    const center = IconSize.HOVERED / 2;
    return buildIconFromSVG(iconSvg, new google.maps.Point(center, center));
  }
}

/**
 * Marker for Busbar assets.
 */
export class BusbarMapMarker extends DefaultMapMarker {
  protected override getDefaultIcon(size: IconSize): string {
    return doubleCircleIconSVG(DEFAULT_ICON_FILL_COLOR, DEFAULT_ICON_STROKE_COLOR, {size});
  }
}
/**
 * Marker for Equivalent Shunt assets.
 *
 * This marker uses a double circle icon.
 */
export class EquivalentShuntMapMarker extends DefaultMapMarker {
  protected override getDefaultIcon(size: IconSize): string {
    return doubleCircleIconSVG(DEFAULT_ICON_FILL_COLOR, DEFAULT_ICON_STROKE_COLOR, {size});
  }
}
/**
 * Marker for Feeder assets.
 */
export class FeederMapMarker extends DefaultMapMarker {
  protected override getDefaultIcon(size: IconSize, opacity = 1): string {
    return feederSVG('#3367D6', 'white', 3, size, opacity);
  }
}
/**
 * Marker for Generating Unit assets.
 */
export class GeneratingUnitMapMarker extends DefaultMapMarker {
  protected override getDefaultIcon(size: IconSize): string {
    return generatorSVG({strokeColor: 'white', fillColor: '#3367D6', size});
  }
}
